<template>
  <AIBasePanel
    auto-scroll
    :title="title"
    @close="emit('close')"
  >
    <AIAssistantBaseChat :messages="messages" />

    <template #footer>
      <AIAssistantBaseChatInput
        ref="inputRef"
        v-model="input"
        data-testid="footer-container"
        :loading="loading"
        :placeholder="getLocaleText('ai_assistant_panel.ask_me_anything')"
        @submit="handleInputSubmit"
      >
        <template #after>
          <BaseButtonDropdown
            circle
            close-on-click
            :disabled="loading"
            icon="list"
            new-design
            new-dropdown
            new-icon
            placement="top-end"
          >
            <div
              class="tw:grid tw:gap-2"
              data-testid="dropdown-content"
            >
              <div class="tw:px-4 tw:py-2 tw:font-bold">
                {{ getLocaleText("ai_assistant_panel.quick_access") }}
              </div>
              <div
                v-for="(group, groupIndex) in optionGroups"
                :key="group.group"
                :data-index="groupIndex"
              >
                <div class="tw:px-4 tw:py-2 tw:font-bold tw:text-denim-600">
                  {{ group.label }}
                </div>
                <BaseButtonList
                  :options="group.options"
                  @update:model-value="handleOptionClick"
                />
              </div>
            </div>
          </BaseButtonDropdown>
        </template>
      </AIAssistantBaseChatInput>
    </template>
  </AIBasePanel>
</template>

<script setup lang="ts">
import { storeToRefs } from "pinia";
import {
  computed,
  inject,
  nextTick,
  onMounted,
  ref,
  useTemplateRef,
  watch,
} from "vue";

import { track } from "shared/boot/analytics";
import { getLocaleText } from "shared/boot/i18n";
import AIAssistantBaseChat from "shared/components/AI/AIAssistant/AIAssistantBaseChat.vue";
import AIAssistantBaseChatInput from "shared/components/AI/AIAssistant/AIAssistantBaseChatInput.vue";
import AIBasePanel from "shared/components/AI/AIBasePanel.vue";
import { BaseButtonDropdown, BaseButtonList } from "shared/components/base";
import { useAIAssistant } from "shared/composables/useAIAssistant";
import useAIAssistantOptions, {
  type AIAssistantOption,
} from "shared/composables/useAIAssistantOptions";
import useAIStore from "shared/stores/ai";

const isMobile = inject<boolean>("isMobile", false);

const emit = defineEmits(["close"]);

const { messages, loading, submit } = useAIAssistant({
  source: "general assistant",
});

const aiStore = useAIStore();
const { aiAssistantPrompt } = storeToRefs(aiStore);
const { openAIModal } = aiStore;

const { optionGroups, hasStreamsCredits } = useAIAssistantOptions(isMobile);

const input = ref<string>("");
const inputRef = useTemplateRef("inputRef");

const title = computed<string>(() => aiAssistantPrompt.value?.label || "");

async function handleOptionClick(option: AIAssistantOption) {
  track("Select Template Button in Assistant Panel", { prompt: option.track });

  const canCreateStreams = await hasStreamsCredits(option);

  if (!canCreateStreams) {
    return;
  }

  if (option.panel) {
    openAIModal(option.panel);
  } else {
    submit(option.label);
  }
}

function handleInputSubmit() {
  track("Send Message in Assistant Panel", {
    template: aiAssistantPrompt.value?.track,
    prompt: input.value,
  });

  submit(input.value);
  input.value = "";
}

watch(loading, async (value) => {
  if (value) return;
  await nextTick();
  inputRef.value?.focus();
});

onMounted(() => {
  if (aiAssistantPrompt.value) {
    submit(aiAssistantPrompt.value.label);
  }
});
</script>

<style scoped lang="scss">
@import "shared/css/components/ai.scss";
</style>
